import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import NewsDetailNavbar from './components/NewsDetailNavbar';
import NewsHero from './components/NewsHero';
import NewsContent from './components/NewsContent';
import RelatedNews from './components/RelatedNews';
import Footer from '@/pages/home/components/Footer';

export interface NewsArticle {
  id: number;
  title: string;
  category: string;
  summary: string;
  content: string;
  cover_image: string;
  author: string;
  tags: string[];
  view_count: number;
  is_published: boolean;
  published_at: string;
  slug: string;
  pinned: boolean;
  read_time: string;
}

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsArticle | null>(null);
  const [allNews, setAllNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      setNotFound(false);

      // 支持通过数字 id 或 slug 查找
      const isNumeric = /^\d+$/.test(id ?? '');
      let query = supabase
        .from('cms_news')
        .select('*')
        .eq('is_published', true);

      if (isNumeric) {
        query = query.eq('id', Number(id));
      } else {
        query = query.eq('slug', id);
      }

      const { data, error } = await query.maybeSingle();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setNews(data as NewsArticle);

      // 增加阅读量
      await supabase
        .from('cms_news')
        .update({ view_count: (data.view_count ?? 0) + 1 })
        .eq('id', data.id);

      // 获取相关新闻
      const { data: relatedData } = await supabase
        .from('cms_news')
        .select('*')
        .eq('is_published', true)
        .neq('id', data.id)
        .order('published_at', { ascending: false })
        .limit(3);

      setAllNews(relatedData as NewsArticle[] ?? []);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (id) fetchNews();
  }, [id]);

  useEffect(() => {
    if (notFound) navigate('/');
  }, [notFound, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  if (!news) return null;

  return (
    <div className="min-h-screen bg-white">
      <NewsDetailNavbar />
      <main>
        <NewsHero news={news} />
        <NewsContent news={news} />
        <RelatedNews relatedNews={allNews} />
      </main>
      <Footer />
    </div>
  );
}
